import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { caseStudies } from "../constant/caseStudies";
import { ClientLogo } from "./ClientLogo";
import "./CaseStudies.css";

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const [study, setStudy] = useState(null);

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);

    const foundStudy = caseStudies.find(
      (cs) => cs.slug === slug || String(cs.id) === slug
    );

    if (foundStudy) {
      setStudy(foundStudy);
      document.title = foundStudy.meta?.title || `${foundStudy.client} Case Study | Tedekstra`;

      let metaTag = document.querySelector("meta[name='description']");
      const description = foundStudy.meta?.description || foundStudy.clientOverview[0];
      if (metaTag) {
        metaTag.setAttribute("content", description);
      } else {
        metaTag = document.createElement("meta");
        metaTag.name = "description";
        metaTag.content = description;
        document.head.appendChild(metaTag);
      }
    }

    if (window.AOS) {
      window.AOS.refresh();
    }
  }, [slug]);

  if (!study) {
    return (
      <div className="container py-5 text-center my-5">
        <h2>Case Study Not Found</h2>
        <p className="text-muted">The requested case study could not be located.</p>
        <Link to="/case-studies" className="btn btn-dark rounded-pill px-4 py-2 mt-3">
          Back to Case Studies
        </Link>
      </div>
    );
  }

  const currentIndex = caseStudies.findIndex(
    (cs) => cs.slug === study.slug || String(cs.id) === String(study.id)
  );
  const nextStudy = caseStudies[(currentIndex + 1) % caseStudies.length];

  return (
    <div className="pdf-view-wrapper">
      <div className="container" style={{ maxWidth: "1000px" }}>
        
        {/* Top Control Bar */}
        <div className="pdf-control-bar">
          <Link to="/case-studies" className="pdf-back-btn">
            <i className="fa-solid fa-arrow-left"></i> All Case Studies
          </Link>

          <div className="pdf-btn-actions">
            <a
              href={study.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="pdf-download-btn"
              style={{ marginRight: "14px" }}
            >
              <i className="fa-regular fa-file-pdf"></i> Download Official PDF
            </a>

            {nextStudy && (
              <Link to={`/case-studies/${nextStudy.slug}`} className="pdf-switch-btn">
                <span>Next: {nextStudy.client}</span> <i className="fa-solid fa-arrow-right"></i>
              </Link>
            )}
          </div>
        </div>

        {/* =========================================================
            PDF SHEET 1 (Page 1)
           ========================================================= */}
        <div className="pdf-page-container">
          <div className="pdf-page-number-indicator">Page 1 / 4</div>
          
          {/* Header with Logo */}
          <div className="pdf-header-row">
            <img src="/images/logo.png" alt="Tedekstra" style={{ height: "38px" }} />
          </div>

          {/* Green Hero Box */}
          <div className="pdf-green-hero">
            <div className="row align-items-center">
              <div className="col-md-7">
                <div className="pdf-hero-headline">CASE STUDY</div>
                <div className="pdf-hero-client">{study.client}</div>
                <div className="pdf-hero-project">{study.subtitle}</div>
              </div>
              <div className="col-md-5 mt-3 mt-md-0">
                <div className="pdf-hero-img-box">
                  <img src={study.image} alt={study.client} />
                </div>
              </div>
            </div>
          </div>

          {/* Client Overview */}
          <div className="row align-items-center mb-4">
            <div className="col-md-8">
              <h2 className="pdf-section-title">Client Overview</h2>
              {study.clientOverview.map((para, idx) => (
                <p key={idx} className="pdf-body-paragraph">{para}</p>
              ))}
            </div>
            <div className="col-md-4 d-flex justify-content-md-end justify-content-center mt-3 mt-md-0">
              <ClientLogo type={study.logoType} />
            </div>
          </div>

          {/* The Challenge */}
          <div className="pdf-outlined-frame">
            <div className="pdf-floating-pill">The Challenge</div>
            <p className="pdf-frame-intro-text">{study.challengeIntro}</p>

            <div className="pdf-2col-chevrons">
              {study.challenges.map((ch, idx) => (
                <div className="pdf-chevron-row" key={idx}>
                  <span className="pdf-chevron-circle pdf-circle-green">
                    <i className="fa-solid fa-chevron-right"></i>
                  </span>
                  <span>{ch}</span>
                </div>
              ))}
            </div>

            <p className="pdf-frame-outro-text">{study.challengeOutro}</p>
          </div>
        </div>

        {/* =========================================================
            PDF SHEET 2 (Page 2)
           ========================================================= */}
        <div className="pdf-page-container">
          <div className="pdf-page-number-indicator">Page 2 / 4</div>

          {/* Header with Logo */}
          <div className="pdf-header-row">
            <img src="/images/logo.png" alt="Tedekstra" style={{ height: "38px" }} />
          </div>

          {/* The Solution */}
          <div className="mb-4">
            <h2 className="pdf-section-title">The Solution</h2>
            <p className="pdf-body-paragraph">{study.solutionIntro}</p>
            <h3 className="pdf-section-title" style={{ fontSize: "1.1rem", marginTop: "16px", marginBottom: "12px" }}>
              The solution included:
            </h3>

            <div className="pdf-2col-chevrons mb-3">
              {study.solutionItems.map((item, idx) => (
                <div className="pdf-chevron-row" key={idx}>
                  <span className="pdf-chevron-circle pdf-circle-coral">
                    <i className="fa-solid fa-chevron-right"></i>
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <p className="pdf-body-paragraph mt-3">{study.solutionOutro}</p>
          </div>

          {/* Implementation Approach */}
          <div className="pdf-outlined-frame">
            <div className="pdf-floating-pill">Implementation Approach</div>
            <p className="pdf-frame-intro-text">{study.implementationIntro}</p>

            <div className="pdf-4phase-grid">
              {study.implementationPhases.map((ph, idx) => (
                <div className="pdf-phase-box" key={idx}>
                  <span className={`pdf-phase-pill-tag ${ph.color === 'pink' ? 'pdf-phase-pill-pink' : 'pdf-phase-pill-blue'}`}>
                    {ph.phase}
                  </span>
                  <div className="pdf-phase-box-title">{ph.title}</div>
                  <ul className="pdf-bullet-list">
                    {ph.points.map((pt, pIdx) => (
                      <li key={pIdx}>{pt}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* =========================================================
            PDF SHEET 3 (Page 3)
           ========================================================= */}
        <div className="pdf-page-container">
          <div className="pdf-page-number-indicator">Page 3 / 4</div>

          {/* Header with Logo */}
          <div className="pdf-header-row">
            <img src="/images/logo.png" alt="Tedekstra" style={{ height: "38px" }} />
          </div>

          {/* Business Outcomes */}
          <div className="pdf-outlined-frame" style={{ marginTop: "10px" }}>
            <div className="pdf-floating-pill">Business Outcomes</div>
            <p className="pdf-frame-intro-text">{study.outcomesIntro}</p>

            <div className="pdf-outcomes-4grid">
              {study.outcomesPillars.map((pillar, idx) => (
                <div className="pdf-outcome-box" key={idx}>
                  <span className="pdf-outcome-header-pill">{pillar.title}</span>
                  <ul className="pdf-bullet-list">
                    {pillar.points.map((pt, pIdx) => (
                      <li key={pIdx}>{pt}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Client Feedback & Key Metrics */}
          <div className="pdf-feedback-metrics-row">
            {/* Left: Client Feedback */}
            <div>
              <h3 className="pdf-quote-heading">Client Feedback</h3>
              <p className="pdf-quote-body">
                "{study.clientFeedback.quote}"
              </p>
              <div className="pdf-quote-signoff">{study.clientFeedback.attribution}</div>
            </div>

            {/* Right: Key Metrics Table */}
            <div>
              <h3 className="pdf-table-heading">Key Project Metrics</h3>
              <table className="pdf-metrics-styled-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {study.keyMetricsTable.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.metric}</td>
                      <td>{row.result}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* =========================================================
            PDF SHEET 4 (Page 4)
           ========================================================= */}
        <div className="pdf-page-container">
          <div className="pdf-page-number-indicator">Page 4 / 4</div>

          {/* Header with Logo */}
          <div className="pdf-header-row">
            <img src="/images/logo.png" alt="Tedekstra" style={{ height: "38px" }} />
          </div>

          {/* Looking Ahead & About Tedekstra */}
          <div className="pdf-page4-grid">
            {/* Left: Looking Ahead framed box */}
            <div className="pdf-outlined-frame" style={{ margin: 0 }}>
              <div className="pdf-floating-pill">Looking Ahead</div>
              {study.lookingAhead.map((para, idx) => (
                <p key={idx} className="pdf-body-paragraph">{para}</p>
              ))}
            </div>

            {/* Right: About Tedekstra */}
            <div>
              <h3 className="pdf-about-bold-title">About Tedekstra</h3>
              <p className="pdf-body-paragraph">
                Tedekstra helps ambitious organisations transform operations through bespoke software solutions, digital transformation, ERP implementations, system integrations, and managed technology services.
              </p>
              <div className="pdf-about-taglines">
                <div>Bespoke Solutions.</div>
                <div>Enterprise Capability.</div>
                <div>No Upfront Development Cost.</div>
              </div>
              <div className="pdf-green-accent-line"></div>
            </div>
          </div>

          {/* Bottom Collaboration Photo */}
          <div className="pdf-bottom-photo-banner">
            <img
              src={study.image || "/images/case-study-emotions-clinic.jpg"}
              alt={`${study.client} Partnership`}
            />
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="pdf-bottom-action-buttons">
          <Link
            to="/case-studies"
            className="btn btn-dark rounded-pill px-4 py-2"
            style={{ background: "#121417", marginRight: "16px" }}
          >
            <i className="fa-solid fa-arrow-left me-2"></i> Back to All Case Studies
          </Link>
          <a
            href={study.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-danger rounded-pill px-4 py-2"
          >
            <i className="fa-regular fa-file-pdf me-2"></i> Download Official PDF
          </a>
        </div>

      </div>
    </div>
  );
};

export default CaseStudyDetail;
